import {
  useGenealogyPerformance,
  useGenealogyPlaces,
  useGenealogyPlots,
  useGenealogyRows,
  useGenealogyTree,
} from "@api/generated/genealogy/genealogy";
import type { GenotypeSheet, Line, Place, PlotEntry } from "@api/generated/model";
import { useWorkspaceSearch } from "@api/generated/workspace/workspace";
import { Icon } from "@app/icons";
import { useSeason } from "@app/workspace";
import { EnvironmentPerformance } from "@features/visualization/EnvironmentPerformance";
import { hint } from "@i18n";
import { Combobox, Loader, SegmentedControl, TextInput, useCombobox } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { Link, useNavigate } from "@tanstack/react-router";
import { cellText, formatNumber } from "@ui/format";
import { Kpi } from "@ui/Kpi";
import { WIDE_LIST, WIDE_LIST_STYLE } from "@ui/SearchSelect";
import { RecentItems, StartCard } from "@ui/StartCard";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import type { CellValue } from "@ui/table/model";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keyParam, keyText, lineTitle, markText, yearBars } from "./genotype";

export const FIELDS = ["trms.atlag", "min.sik", "min.esessz", "trms.tkw"] as const;
type Key = { paz: number; sid: number };

/** A link to a line's sheet (or plain text for a name the register does not know). */
export function LineLink({ line, text }: { line: Line | null | undefined; text?: string | null }) {
  if (!line) return <span>{text ?? "—"}</span>;
  return (
    <Link to="/genealogy/genotype" search={{ paz: line.key.paz, sid: line.key.sid }}>
      {line.name ?? text ?? keyText(line.key)}
    </Link>
  );
}

/** The "PAz / SID" column of a table of lines: the key as the breeders write it. */
export function keyColumn<T>(key: (row: T) => Key, id = "key"): Column<T> {
  return { id, header: "PAz / SID", value: (r) => keyText(key(r)), className: "mono" };
}

/** A column of lines by their title (the name, else the key), each a link to its sheet. */
export function lineColumn<T>(id: string, header: string, line: (row: T) => Line): Column<T> {
  return { id, header, value: (r) => lineTitle(line(r)), cell: (r) => <LineLink line={line(r)} /> };
}

/** The quick search's genotypes for a typed text (from 2 characters, a moment after typing). */
function useGenotypeHits(q: string) {
  const { ev } = useSeason();
  const [asked] = useDebouncedValue(q.trim(), 250);
  const hits = useWorkspaceSearch({ q: asked, ev: ev ?? 0, kind: "genotype" }, { query: { enabled: asked.length >= 2 && !!ev } });
  return { asked, hits };
}

/** A line found by name, pedigree or key (the quick search's genotypes), handed to ``onPick``. */
export function LinePicker(props: {
  label: string;
  onPick: (key: Key, title: string) => void;
  size?: "xs" | "md";
  autoFocus?: boolean;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const { asked, hits } = useGenotypeHits(q);
  const combobox = useCombobox();
  const short = asked.length < 2; // too little typed to search: the list says how to
  return (
    <Combobox
      store={combobox}
      {...WIDE_LIST}
      onOptionSubmit={(v) => {
        combobox.closeDropdown();
        const h = hits.data?.find((x) => keyParam(x.ref as Key) === v);
        if (h) props.onPick(h.ref as Key, h.title);
        setQ("");
      }}
    >
      <Combobox.Target>
        <TextInput
          size={props.size ?? "xs"}
          label={props.label}
          placeholder={props.placeholder ?? hint("searchTwo")}
          value={q}
          autoFocus={props.autoFocus}
          leftSection={props.size === "md" ? <Icon name="search" size={16} /> : undefined}
          onChange={(e) => {
            setQ(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={hits.isFetching ? <Loader size="xs" /> : null}
        />
      </Combobox.Target>
      {/* the guidance is in the list (not a description squeezed under a narrow field), the list wider than the field */}
      <Combobox.Dropdown hidden={!short && !hits.data} style={WIDE_LIST_STYLE}>
        <Combobox.Options>
          {short && <Combobox.Empty>{hint("searchTwoHelp")}</Combobox.Empty>}
          {(short ? [] : (hits.data ?? [])).map((h) => (
            <Combobox.Option key={keyParam(h.ref as Key)} value={keyParam(h.ref as Key)}>
              <b>{h.title}</b> <span className="mono mu sm">{h.subtitle}</span>
            </Combobox.Option>
          ))}
          {!short && hits.data?.length === 0 && <Combobox.Empty>{t("genotype.noMatches")}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

/** Finding a line to show: the quick search's genotype hits (name, pedigree, PAz / SID). */
export function GenotypeSearch({ to }: { to: "/genealogy/genotype" | "/genealogy/tree" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <StartCard title={t("genotype.startTitle")} description={t("genotype.startDescription")}>
      <LinePicker
        size="md"
        autoFocus
        label={t("genotype.search")}
        placeholder={t("genotype.searchHelp")}
        onPick={(key) => void navigate({ to, search: { paz: key.paz, sid: key.sid } })}
      />
      <RecentItems kind="genotype" max={6} filter={(r) => r.href.startsWith(to)} />
      <div className="hstack start-links">
        <span className="mu">{t("genotype.startOther")}</span>
        <Link to="/genealogy/register">{t("nav.genealogy.register")}</Link>
        {to !== "/genealogy/tree" && <Link to="/genealogy/tree">{t("nav.genealogy.tree")}</Link>}
        <Link to="/genealogy/gene-search">{t("nav.genealogy.geneSearch")}</Link>
        <Link to="/genealogy/ancestors">{t("nav.genealogy.ancestors")}</Link>
      </div>
    </StartCard>
  );
}

/** The line's gene-bank items left out of the totals: "1 not weighed · 15 bags not counted" (or nothing). */
function genebankUnknown(s: GenotypeSheet["summary"], t: TFunction): string {
  return [
    s.genebank_unweighed ? t("genotype.genebankUnweighed", { count: s.genebank_unweighed }) : "",
    s.genebank_uncounted ? t("genotype.genebankUncounted", { count: s.genebank_uncounted }) : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/** How the GeneBank figures are made, above the line's gene-bank rows, so they can be checked against them. */
export function GenebankTotals({ sheet }: { sheet: GenotypeSheet }) {
  const { t } = useTranslation();
  const s = sheet.summary;
  if (!s.genebank_items) return null;
  return (
    <p className="note">
      {t("genotype.genebankHow", { kg: formatNumber(s.genebank_kg ?? 0, 2), count: s.genebank_items, bags: s.genebank_bags })}
      {s.genebank_unweighed ? ` ${t("genotype.genebankHowUnweighed", { count: s.genebank_unweighed })}` : ""}
      {s.genebank_uncounted ? ` ${t("genotype.genebankHowUncounted", { count: s.genebank_uncounted })}` : ""}
    </p>
  );
}

/** The sheet's key figures; the ones with a tab of their own open it (``onTab``). */
export function Kpis({ sheet, year, onTab }: { sheet: GenotypeSheet; year: number; onTab: (tab: "years" | "genebank") => void }) {
  const { t } = useTranslation();
  const s = sheet.summary;
  const kg = s.genebank_kg ? formatNumber(s.genebank_kg, 2) : null;
  const unknown = genebankUnknown(s, t);
  return (
    <div className="kpis">
      <Kpi
        label={t("genotype.firstAppearance")}
        value={String(s.first_year ?? "—")}
        sub={sheet.register.cross_ev ? t("genotype.cross", { ev: sheet.register.cross_ev }) : undefined}
      />
      <Kpi
        label={t("genotype.plots")}
        value={formatNumber(s.plots)}
        sub={t("genotype.yearsSites", { ev: s.years, th: s.sites })}
        onClick={() => onTab("years")}
      />
      <Kpi
        label={t("genotype.inYear", { ev: year })}
        value={t("genotype.plotCount", { count: formatNumber(s.year_plots) })}
        sub={t("genotype.trialCount", { count: s.year_trials })}
        onClick={() => onTab("years")}
      />
      <Kpi
        label={t("genotype.genebank")}
        value={kg ? `${kg} kg` : t("genotype.itemCount", { count: s.genebank_items })}
        sub={[t("genotype.genebankItems", { count: s.genebank_items, bags: s.genebank_bags }), unknown].filter(Boolean).join(" · ")}
        onClick={() => onTab("genebank")}
      />
    </div>
  );
}

/** The line's yearly means next to the trials' means, one field at a time; a click on a year chooses it. */
function AnnualPerformanceCard({ k, onYear }: { k: Key; onYear: (ev: number) => void }) {
  const { t } = useTranslation();
  const [field, setField] = useState<string>(FIELDS[0]);
  const data = useGenealogyPerformance(k.paz, k.sid, { fields: [...FIELDS] }).data ?? [];
  const { bars, top } = yearBars(data, field);
  const label = t(`genotype.field.${field.replace(".", "_")}`);
  return (
    <section className="card pad stack" style={{ gap: 10 }}>
      <div className="hstack" style={{ gap: 12 }}>
        <h2 className="h2 grow">{t("genotype.performance")}</h2>
        <SegmentedControl
          size="xs"
          value={field}
          onChange={setField}
          data={FIELDS.map((f) => ({ value: f, label: t(`genotype.field.${f.replace(".", "_")}`) }))}
        />
      </div>
      {bars.length === 0 ? (
        <p className="sm mu">{t("genotype.noMeasurements", { field: label })}</p>
      ) : (
        <div className="gt-bars" role="img" aria-label={t("genotype.performanceLabel", { field: label })}>
          {bars.map((b) => (
            <button
              type="button"
              key={b.ev}
              className="gt-bar"
              onClick={() => onYear(b.ev)}
              title={t("genotype.barHelp", { ev: b.ev, count: b.plots })}
            >
              <span className="gt-bar-pair">
                <span className="mine" style={{ height: `${(100 * (b.value ?? 0)) / top}%` }} />
                <span className="mean" style={{ height: `${(100 * (b.trial_mean ?? 0)) / top}%` }} />
              </span>
              <span className="mono sm">{formatNumber(b.value, 1)}</span>
              <span className="mono mu sm">{b.ev}</span>
            </button>
          ))}
        </div>
      )}
      <div className="hstack sm" style={{ gap: 14 }}>
        <span className="gt-legend mine">{t("genotype.legendLine")}</span>
        <span className="gt-legend mean">{t("genotype.legendTrialMean")}</span>
        <span className="mu">{t("genotype.clickYearHint")}</span>
      </div>
    </section>
  );
}

export function PlacesCard({ k, year }: { k: Key; year: number }) {
  const { t } = useTranslation();
  const places = useGenealogyPlaces(k.paz, k.sid, { ev: year });
  const columns = useMemo<Column<Place>[]>(
    () => [
      {
        id: "location",
        header: t("genotype.location"),
        value: (p) => (p.kind === "genebank" ? t("genotype.genebank") : t("genotype.nursery", { th: p.th ?? "—" })),
      },
      {
        id: "trial",
        header: t("genotype.trialOrStorage"),
        value: (p) => p.kis,
        className: "mono",
        cell: (p) =>
          p.kind === "field" ? (
            <Link to="/trials/data" search={{ ev: year, table: "trms", kis: p.kis, th: p.th ?? undefined }}>
              {p.kis}
            </Link>
          ) : (
            p.kis
          ),
      },
      { id: "plots", header: t("genotype.plots"), value: (p) => p.plots, numeric: true },
      {
        id: "yield",
        header: t("genotype.yield"),
        value: (p) => p.value,
        numeric: true,
        cell: (p) => formatNumber(p.value, 2),
      },
      {
        id: "relative",
        header: t("genotype.standardPercent"),
        value: (p) => p.relative,
        numeric: true,
        cell: (p) => formatNumber(p.relative, 1),
      },
      {
        id: "remark",
        header: t("genotype.remark"),
        value: (p) =>
          p.kind === "genebank"
            ? t("genotype.genebankQuantity", { kg: formatNumber(p.amount_kg ?? 0, 2), bags: p.bags ?? 0 })
            : p.standard
              ? t("genotype.standard")
              : "",
        className: "sm mu",
      },
    ],
    [t, year],
  );
  const table = useDataTable("genealogy.genotype.places", columns, places.data);
  return (
    <section className="card">
      <div className="hstack inset" style={{ gap: 10, paddingBottom: 6 }}>
        <h2 className="h2">{t("genotype.whereabouts", { ev: year })}</h2>
        <span className="sm mu">{t("genotype.whereaboutsHelp")}</span>
        {places.isFetching && <Loader size="xs" />}
      </div>
      <DataTable
        table={table}
        rowKey={(p) => `${p.kind}-${p.th}-${p.kis}`}
        empty={places.data && <span className="sm mu">{t("genotype.noLocation", { ev: year })}</span>}
      />
    </section>
  );
}

/** The line's parents and its largest ancestors (the tree read five generations deep, apart from the sheet). */
export function PedigreeCard({ sheet }: { sheet: GenotypeSheet }) {
  const { t } = useTranslation();
  const k = sheet.register;
  const ancestry = useAncestry(k).data?.ancestry ?? [];
  return (
    <section className="card pad stack" style={{ gap: 8 }}>
      <div className="hstack">
        <h2 className="h2 grow">{t("genotype.pedigree")}</h2>
        <Link to="/genealogy/tree" search={{ paz: k.paz, sid: k.sid }} className="sm">
          {t("genotype.pedigreeTree")}
        </Link>
      </div>
      <div className="gt-mini">
        <b>{k.name ?? keyText(k)}</b>
        <div className="gt-mini-parents">
          <span>
            ♀ <LineLink line={sheet.mother} text={sheet.mother_text} />
          </span>
          <span>
            ♂ <LineLink line={sheet.father} text={sheet.father_text} />
          </span>
        </div>
      </div>
      {ancestry.length > 0 && (
        <span className="sm mu">
          {t("genotype.ancestors")}:{" "}
          {ancestry
            .slice(0, 4)
            .map((a) => `${a.label ?? "?"} ${formatNumber(a.share, 1)} %`)
            .join(" · ")}
        </span>
      )}
    </section>
  );
}

/** The ancestry the sheet shows: the tree five generations deep (the legacy Analyze depth). */
export const useAncestry = (k: Key) => useGenealogyTree(k.paz, k.sid, { depth: 5 }, { query: { staleTime: 300_000 } });

export function SistersCard({ sheet }: { sheet: GenotypeSheet }) {
  const { t } = useTranslation();
  return (
    <section className="card pad stack" style={{ gap: 4 }}>
      <div className="hstack">
        <h2 className="h2 grow">{t("genotype.sisterLines")}</h2>
        <span className="sm mu">PAz {sheet.register.paz}</span>
      </div>
      {sheet.sisters.map((s) => (
        <div key={s.key.sid} className="gt-sister">
          <span className="mono mu">{s.key.sid}</span>
          {s.key.sid === sheet.register.sid ? <b>{lineTitle(s)}</b> : <LineLink line={s} />}
          <span className="mono mu sm">{s.line_name ?? (s.variety ? t("genotype.variety") : t("genotype.line"))}</span>
        </div>
      ))}
    </section>
  );
}

export function MarkersCard({ sheet }: { sheet: GenotypeSheet }) {
  const { t } = useTranslation();
  const chips = sheet.chips.map((m) => ({ ...markText(m), locus: m.locus }));
  const s = sheet.summary;
  return (
    <section className="card pad stack" style={{ gap: 8 }}>
      <h2 className="h2">{t("genotype.molecularMarkers")}</h2>
      {chips.length ? (
        <div className="chips">
          {chips.map((c) => (
            <span key={c.locus} className={`chip ${c.tone}`}>
              {c.locus} {c.mark}
            </span>
          ))}
        </div>
      ) : (
        <span className="sm mu">{t("genotype.noMarkers")}</span>
      )}
      <span className="sm mu">{t("genotype.dnaSamples", { count: s.dna_samples, ev: s.last_dna_year ?? "—" })}</span>
    </section>
  );
}

/** Every plot of the line, the newest year first. */
export function PlotsTab({ k }: { k: Key }) {
  const { t } = useTranslation();
  const plots = useGenealogyPlots(k.paz, k.sid).data;
  const columns = useMemo<Column<PlotEntry>[]>(
    () => [
      { id: "year", header: t("genotype.year"), value: (p) => p.ev, text: (p) => String(p.ev), className: "mono" },
      {
        id: "trial",
        header: t("genotype.trial"),
        value: (p) => p.kis,
        className: "mono",
        cell: (p) => (
          <Link to="/trials/data" search={{ ev: p.ev, table: "trms", kis: p.kis }}>
            {p.kis}
          </Link>
        ),
      },
      { id: "plot", header: t("genotype.plot"), value: (p) => p.parc, text: (p) => String(p.parc), numeric: true },
      { id: "code", header: t("genotype.code"), value: (p) => p.kod, className: "mono sm" },
      {
        id: "source",
        header: t("genotype.source"),
        value: (p) => (p.e_kis ? `${p.e_kis} ${p.e_parc ?? ""}` : ""),
        className: "mono sm mu",
      },
      {
        id: "standard",
        header: t("genotype.standardColumn"),
        value: (p) => (p.standard ? t("genotype.standard") : ""),
        cell: (p) => p.standard && <span className="chip b">{t("genotype.standard")}</span>,
      },
    ],
    [t],
  );
  const table = useDataTable("genealogy.genotype.plots", columns, plots);
  if (!plots) return <Loader size="sm" />;
  return (
    <section className="card">
      <DataTable table={table} rowKey={(p) => `${p.ev}-${p.kis}-${p.parc}`} />
    </section>
  );
}

/** A row of a line's table, numbered: the server lists the rows without a key of their own. */
type ValueRow = { at: number; cells: unknown[] };
const cellValue = (v: unknown): CellValue => (v == null || typeof v !== "object" ? (v as CellValue) : JSON.stringify(v));

/** A line's rows of a table that hangs on it, the columns under their legacy labels. */
export function RowsTab({ k, table: name }: { k: Key; table: string }) {
  const { t } = useTranslation();
  const data = useGenealogyRows(k.paz, k.sid, name).data;
  const rows = useMemo(() => data?.rows.map((cells, at): ValueRow => ({ at, cells })), [data]);
  const columns = useMemo<Column<ValueRow>[]>(
    () =>
      (data?.columns ?? []).map((id, j) => ({
        id,
        header: data?.labels[j] ?? id,
        value: (r) => cellValue(r.cells[j]),
        text: (r) => cellText(r.cells[j]),
        // a column of numbers only is right-aligned and takes the number filters (>5, 2..8)
        numeric: !!data?.rows.some((r) => r[j] != null) && !!data?.rows.every((r) => r[j] == null || typeof r[j] === "number"),
      })),
    [data],
  );
  const table = useDataTable(`genealogy.genotype.rows.${name}`, columns, rows);
  if (!data) return <Loader size="sm" />;
  if (!data.rows.length) return <p className="sm mu">{t("genotype.noRows")}</p>;
  return (
    <section className="card">
      <DataTable table={table} rowKey={(r) => r.at} />
    </section>
  );
}

export function PerformanceCard({
  k,
  onYear,
  mode,
  onMode,
}: {
  k: Key;
  onYear: (ev: number) => void;
  mode: "environment" | "annual";
  onMode: (mode: "environment" | "annual") => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="stack">
      <SegmentedControl
        aria-label={t("genotype.performanceView")}
        value={mode}
        onChange={(v) => onMode(v as "environment" | "annual")}
        data={[
          { value: "environment", label: t("genotype.performanceByEnvironment") },
          { value: "annual", label: t("genotype.performanceYearly") },
        ]}
      />
      {mode === "environment" ? (
        <EnvironmentPerformance paz={k.paz} sid={k.sid} />
      ) : (
        <>
          <p className="sm mu">{t("genotype.performanceYearlyHelp")}</p>
          <AnnualPerformanceCard k={k} onYear={onYear} />
        </>
      )}
    </div>
  );
}
